import React, { useState, useEffect } from 'react';
import Header from './Header'; // Import Header component
import Sidebar from './Sidebar'; // Import Sidebar component
import './AppoinmentHistory.css'; // Ensure you create this CSS file for styling
import './Dashboard.css'; // Ensure you create this CSS file for styling
import { faChevronRight, faEdit, faTrash, faEye,  faCircleCheck, faCircle, faFileCsv } from '@fortawesome/free-solid-svg-icons'; // Import right arrow icon
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {axiosInstance} from './helper/axiosConfig';
import { Link, useLocation, useNavigate } from 'react-router-dom'; // Import Link and useLocation for navigation
import { isAdmin, isDoctor } from './helper/authRoles';
import toast, { Toaster } from 'react-hot-toast';
// Function to generate random colors for avatars
const getRandomColor = () => {
  const colors = ['#f56565', '#68d391', '#63b3ed', '#ed8936', '#f6ad55', '#9f7aea', '#48bb78', '#4299e1'];
  return colors[Math.floor(Math.random() * colors.length)];
};

// Utility function to get the first letter of the appoinment's name
const getAvatarLetter = (name) => {
  return name ? name.charAt(0).toUpperCase() : '?';
};


function AppoinmentHistory() {
  const [appoinmentHistory, setAppoinmentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10; // Set rows per page for pagination
  const [totalPages, setTotalPages] = useState(1); // Track total pages
  const [searchTerm, setSearchTerm] = useState(''); // State for search term
  // const [isExpired, setIsExpired] = useState(false);
  const currentDate = new Date();
  const tenYearsAgo = new Date(currentDate.setFullYear(currentDate.getFullYear() - 10)); // Calculate the date 10 years ago
  const history = useNavigate();

  const [dateRange, setDateRange] = useState({
    startDate: tenYearsAgo,
    endDate: new Date(),
  });

  const isDateExpired = (dateString) => {
      const appointmentDate = new Date(dateString);

      // Set the time for both dates to midnight to only compare the date, not the time
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Check if appointmentDate is strictly before today
      return appointmentDate < today;
  };


  // Fetch appoinment history data with pagination and filtering
  const fetchAppoinmentHistory = async (page, searchTerm, dateRange) => {
    try {
      const response = await axiosInstance.get(`/api/appoinment-all/`, {
        params: {
          page: page, // Pass the current page
          limit: rowsPerPage, // Pass the rows per page
          search: searchTerm, // Pass the search term
          startDate: dateRange.startDate.toISOString(), // Pass the start date
          endDate: dateRange.endDate.toISOString(), // Pass the end date
        },
      });
      setAppoinmentHistory(response.data.results); // Assuming the API returns a `results` array
      setTotalPages(Math.ceil(response.data.total / rowsPerPage)); // Assuming the API returns a `total` count of appoinments
    } catch (error) {
      console.error('Error fetching appoinment history:', error);
      setError('Failed to fetch appoinment history.');
    } finally {
      setLoading(false); // Stop the loading state
    }
  };


  // const handleDeleteAppointment = async (appointmentId) => {
  //   const r = window.confirm("Are you sure to remove the appoinemtn?");
  //   if(r){
  //     try {
  //       await axiosInstance.delete(`/api/appoinment/${appointmentId}`);
  //       // Handle successful deletion, e.g., update the list of appointments, show a success message
  //       console.log('Appointment deleted successfully');
  //       fetchAppoinmentHistory(currentPage, searchTerm, dateRange);
  //       //history('/appoinment-history'); // Redirect after successful creation
  //     } catch (error) {
  //       console.error('Error deleting appointment:', error);
  //       // Handle error, e.g., show an error message to the user
  //     }
  //   }
  // };

  const handleCancelAppointment = async (appointmentId) => {
    const r = window.confirm("Are you sure you want to cancel the appointment?");
    if (r) {
      try {
        await axiosInstance.put(`/api/appoinment/${appointmentId}/cancel/`);
        // Handle successful cancellation
        console.log("Appointment canceled successfully");
        toast.success('Appointment canceled successfully.');
        fetchAppoinmentHistory(currentPage, searchTerm, dateRange);
      } catch (error) {
        console.error("Error canceling appointment:", error);
        // Handle error, e.g., show an error message to the user
        alert("Failed to cancel the appointment. Please try again.");
      }
    }
  };

  const exportToCSV = () => {
    if (!appoinmentHistory || appoinmentHistory.length === 0) {
      alert('No data available to export.');
      return;
    }

    const headers = [
      'Patient Name',
      'Patient Number',
      "Doctor's Name",
      'Speciality',
      'Date',
      'Time',
    ];

    const rows = appoinmentHistory.map((appoinment) => [
      appoinment.patient_name,
      appoinment.patient_number,
      appoinment.doctor?.full_name || 'N/A',
      appoinment.speaciality,
      appoinment.date
        ? new Date(appoinment.date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })
        : 'N/A',
      appoinment.time || 'N/A',
    ]);

    let csvContent = 'data:text/csv;charset=utf-8,';
    csvContent += headers.join(',') + '\n';
    rows.forEach((row) => {
      csvContent += row.join(',') + '\n';
    });

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', 'appointment_history.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    fetchAppoinmentHistory(currentPage, searchTerm, dateRange);
  }, [currentPage, searchTerm, dateRange]);

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // New function to handle search and date changes
  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1); // Reset to first page on search
  };

  const handleDateChange = (ranges) => {
    setDateRange({
      startDate: ranges.startDate,
      endDate: ranges.endDate,
    });
    setCurrentPage(1); // Reset to first page on date change
  };

  return (
    <div className="dashboard-container">
      <Sidebar /> {/* Sidebar component */}
      <div className="dashboard-main">
        <Header onSearch={handleSearch} onDateChange={handleDateChange} /> {/* Pass handlers to Header */}
        <div className="content-box">
          <div className="appoinment-history-container">
            <div className="header-container">
            <>
              <h1>{isAdmin() && ( <>Appointment’s List</> )} {isDoctor() && ( <>My Appointment</> )}</h1>
              <div>
              {isAdmin() && (
                <button className="btn print-btn">
                  <Link to="/create-appoinment/"> + Add Appoinment</Link>
                </button>
              )}
              <button onClick={exportToCSV} className="export-button">
                <FontAwesomeIcon icon={faFileCsv} className="picker-icon" /> Export CSV 
              </button>
              </div>
            </>
            </div>

            {loading ? (
              <p>Loading...</p>
            ) : error ? (
              <p>{error}</p>
            ) : (
              <>
                {appoinmentHistory && appoinmentHistory.length > 0 && (
                <>
                <table className="appoinment-history-table">
                  <thead>
                    <tr>
                      <th>Patient's Name</th>
                      <th>Patient Number</th>
                      <th>Doctor's Name</th>
                      <th>Speciality</th>
                      <th>Date</th>
                      <th>Time</th>
                      {isAdmin() && (<th>&nbsp;</th>)}
                      {isAdmin() && (<th>&nbsp;</th>)}
                      {isDoctor() && (<th>Status</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {appoinmentHistory.map((appoinment) => (
                      <tr key={appoinment.id}>
                        <td>
                          {/* Avatar circle */}
                          <div className="avatar-circle" style={{ backgroundColor: !appoinment?.patient?.profile_pic ? getRandomColor() : 'transparent' }}>
                            {appoinment?.patient?.profile_pic ? (
                              <img
                                src={`${process.env.REACT_APP_AXIOS_BASE_URL}${appoinment?.patient?.profile_pic}`}
                                alt={appoinment?.patient?.name}
                                className="avatar-circle"
                              />
                            ) : (
                              <span className="initials">{getAvatarLetter(appoinment?.patient_name)}</span>
                            )}
                          </div>

                          {appoinment.patient_name}
                        </td>
                        <td>{appoinment.patient_number}</td>
                        <td>
                          {appoinment.doctor?.title ? appoinment.doctor.title : "N/A"} {appoinment.doctor?.full_name ? appoinment.doctor.full_name : "N/A"}
                        </td>
                        <td>{appoinment.speaciality}</td>
                        <td>
                          {appoinment.date
                            ? `${new Date(appoinment.date).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long', // or 'short' for shorter month names
                                day: 'numeric',
                              })}`
                            : "N/A"}
                        </td>
                        <td>
                          {appoinment.time ? (
                              (() => {
                                const [hours, minutes, seconds] = appoinment.time.split(':');
                                const date = new Date();
                                date.setHours(parseInt(hours, 10), parseInt(minutes, 10), parseInt(seconds, 10));
                                return date.toLocaleTimeString('en-US', {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                });
                              })()
                            ) : (
                              "N/A"
                            )}
                        </td>
                        <td>
                          <div>
                            {isAdmin() && (
                              <>
                              { !isDateExpired(appoinment.date) && !appoinment.prescription && ( // Call the function here
                                <Link to={`/update-appoinment/${appoinment.id}`} className="picker-link">
                                  <FontAwesomeIcon icon={faEdit} className="picker-icon" />
                                </Link>
                              )}
                              </>
                            )}
                            {isDoctor() && (
                              <FontAwesomeIcon
                                icon={isDateExpired(appoinment.date) || appoinment.prescription ? faCircleCheck : faCircle}
                                className={`picker-icon ${isDateExpired(appoinment.date) ? 'expired' : ''}`}
                                title={isDateExpired(appoinment.date) ? 'Appointment Expired' : 'Appointment Active'}
                              />
                            )}
                          </div>
                        </td>
                        {isAdmin() && (
                          <td>
                          { !isDateExpired(appoinment.date) && !appoinment.prescription && ( // Call the function here
                              <a onClick={() => handleCancelAppointment(appoinment.id)} className="trash-icon">
                                <FontAwesomeIcon icon={faTrash} className="picker-icon" />
                              </a>
                          )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="pagination">
                {totalPages > rowsPerPage && (
                  <>
                    {currentPage > 1 && (
                      <button onClick={() => handlePageChange(currentPage - 1)} className="ltgt">&lt;</button>
                    )}
                    {Array.from({ length: totalPages }, (_, index) => {
                      if (index + 1 === currentPage || index + 1 === 1 || index + 1 === totalPages || Math.abs(currentPage - (index + 1)) <= 1) {
                        return (
                          <button
                            key={index + 1}
                            onClick={() => handlePageChange(index + 1)}
                            className={currentPage === index + 1 ? 'active' : ''}
                          >
                            {index + 1}
                          </button>
                        );
                      } else if (Math.abs(currentPage - (index + 1)) === 2) {
                        return <span key={index} className="dots">...</span>;
                      }
                      return null;
                    })}
                    {currentPage < totalPages && (
                      <button onClick={() => handlePageChange(currentPage + 1)} className="ltgt">&gt;</button>
                    )}
                    </>
                  )}
                </div>
                </>
                )}
              {appoinmentHistory && appoinmentHistory.length === 0 && (
                <p>No appointment found.</p>
              )}
              </>
            )}
          </div>
        </div>
        <div className="footer-content">
            {process.env.REACT_APP_COPYRIGHT_TEXT}
        </div>
      </div>
    </div>
  );
}

export default AppoinmentHistory
